import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    FaUserPlus, FaSearch, FaEdit, FaTrash, FaIdCard,
    FaCheckCircle, FaTimesCircle, FaUsers, FaStar,
    FaEye, FaFilter, FaTrophy, FaSyncAlt, FaEnvelope
} from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "universal-cookie";
import moment from "moment";
import "../../css/TelefonosNotificacion.css";
import "../../css/Usuarios.css";
import "../../css/Prospectos.css";

// ════════════════════════════════════════════════════════
// CONSTANTES
// ════════════════════════════════════════════════════════

const ESTADOS_EDITABLES = ["Nuevo", "Contactado", "Interesado", "Descartado"];
const TODOS_LOS_ESTADOS = ["Nuevo", "Contactado", "Interesado", "Exitoso", "Descartado"];

const BADGE_CLASS = {
    Nuevo:      "badge-estado badge-nuevo",
    Contactado: "badge-estado badge-contactado",
    Interesado: "badge-estado badge-interesado",
    Exitoso:    "badge-estado badge-exitoso",
    Descartado: "badge-estado badge-descartado",
};

const MESES = [
    "Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

// ════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════

const esBloqueado = (p) => p.estado === "Exitoso" && p.docuMetoVerificado;

// ════════════════════════════════════════════════════════
// COMPONENTE
// ════════════════════════════════════════════════════════
const Prospectos = () => {
    const cookies  = new Cookies();
    const baseURL  = process.env.REACT_APP_API_BASE_URL;

    const userName = cookies.get("nombreUsuario") || "";
    const perfil   = cookies.get("perfil")        || "";
    const esAdmin  = perfil === "Administrador";

    // ── Datos ──────────────────────────────────────────────
    const [datos,          setDatos]     = useState([]);
    const [datosFiltrados, setFiltrados] = useState([]);
    const [paginaActual,   setPagina]    = useState(1);
    const [asesores,       setAsesores]  = useState([]);
    const elementosPorPagina = 10;

    // ── Verificación automática ────────────────────────────
    const [verificando,    setVerificando]    = useState(false);
    const verificacionRef = useRef(false);

    // ── Correo ─────────────────────────────────────────────
    const [enviandoCorreo,        setEnviandoCorreo]        = useState(null);
    const [enviarCorreoAlGuardar, setEnviarCorreoAlGuardar] = useState(false);

    // ── Filtros de grilla ──────────────────────────────────
    const [busqueda,     setBusqueda]     = useState("");
    const [estadoFiltro, setEstadoFiltro] = useState("");
    const [asesorFiltro, setAsesorFiltro] = useState("");
    const [fechaInicio,  setFechaInicio]  = useState("");
    const [fechaFin,     setFechaFin]     = useState("");

    // ── Dashboard ──────────────────────────────────────────
    const [mesDash,    setMesDash]    = useState(moment().month() + 1);
    const [anioDash,   setAnioDash]   = useState(moment().year());
    const [asesorDash, setAsesorDash] = useState("");

    // ── Modales ────────────────────────────────────────────
    const [modalVisible,    setModalVisible]    = useState(false);
    const [modoEdicion,     setModoEdicion]     = useState(false);
    const [prospectoSel,    setProspectoSel]    = useState(null);
    const [modalDetVisible, setModalDetVisible] = useState(false);

    // ── Match documento (modal edición) ───────────────────
    const [matchCedula, setMatchCedula] = useState(null);
    const [buscandoCed, setBuscandoCed] = useState(false);

    // ── Plan para modal de detalle ─────────────────────────
    // Se carga al abrir el detalle de un prospecto Exitoso
    const [planDetalle,         setPlanDetalle]         = useState(null);
    const [cargandoPlanDetalle, setCargandoPlanDetalle] = useState(false);

    const formularioVacio = {
        nroDocumento: "", nombres: "", telefono: "",
        correo: "", estado: "Nuevo", observaciones: "", nomina: "NO",
    };
    const [formulario, setFormulario] = useState(formularioVacio);

    // ════════════════════════════════════════════════════
    // FETCH
    // ════════════════════════════════════════════════════
    const fetchProspectos = useCallback(async (mantenerPagina = false) => {
        try {
            const resp = await axios.get(`${baseURL}/api/Prospectos/ObtenerProspectos`, {
                params: { asesor: userName, esAdmin }
            });
            const lista = (resp.data.data || []).map(p =>
                p.estado === "Exitoso" ? { ...p, docuMetoVerificado: true } : p
            );
            setDatos(lista);
            if (!mantenerPagina) setPagina(1);
            return lista;
        } catch (err) {
            console.error(err);
            Swal.fire({
                toast: true, position: "top-end", icon: "error",
                title: "Error al cargar prospectos", showConfirmButton: false, timer: 3000
            });
            return [];
        }
    }, [baseURL, esAdmin, userName]);

    const fetchAsesores = useCallback(async () => {
        if (!esAdmin) return;
        try {
            const resp = await axios.get(`${baseURL}/api/Usuarios/ObtenerUsuarios`);
            setAsesores(resp.data.data || []);
        } catch (err) { console.error(err); }
    }, [baseURL, esAdmin]);

    // ════════════════════════════════════════════════════
    // VERIFICACIÓN AUTOMÁTICA DE MATCHES
    // ════════════════════════════════════════════════════
    const verificarMatchesAutomatico = useCallback(async (listaProspectos) => {
        if (verificacionRef.current) return;
        verificacionRef.current = true;
        setVerificando(true);

        const pendientes = listaProspectos.filter(p => !esBloqueado(p) && p.estado !== "Exitoso");

        if (pendientes.length === 0) {
            setVerificando(false);
            verificacionRef.current = false;
            return;
        }

        const exitososNuevos = [];
        const chunks = [];
        for (let i = 0; i < pendientes.length; i += 5)
            chunks.push(pendientes.slice(i, i + 5));

        for (const chunk of chunks) {
            await Promise.all(
                chunk.map(async (p) => {
                    try {
                        const resp = await axios.get(
                            `${baseURL}/api/Prospectos/BuscarSuscriptorPorNroDocumento`,
                            { params: { nroDocumento: p.nroDocumento } }
                        );
                        const match = resp.data;
                        if (match && !match.noMatch) {
                            try {
                                await axios.put(`${baseURL}/api/Prospectos/ActualizarProspecto`, {
                                    id: p.id, nombres: p.nombres, telefono: p.telefono,
                                    correo: p.correo, estado: "Exitoso",
                                    observaciones: p.observaciones, documentoVerificado: true,
                                    nomina: p.nomina ?? p.Nomina ?? "NO",
                                });
                                exitososNuevos.push({ id: p.id, plan: match.plan || null });
                            } catch (putErr) {
                                console.error(`[AutoMatch] PUT id=${p.id}:`, putErr?.response?.data || putErr.message);
                            }
                        }
                    } catch (getErr) {
                        console.error(`[AutoMatch] GET doc=${p.nroDocumento}:`, getErr?.response?.data || getErr.message);
                    }
                })
            );
        }

        if (exitososNuevos.length > 0) {
            const idsExitosos = exitososNuevos.map(e => e.id);
            setDatos(prev => prev.map(p => {
                const encontrado = exitososNuevos.find(e => e.id === p.id);
                return encontrado
                    ? { ...p, estado: "Exitoso", docuMetoVerificado: true, plan: encontrado.plan }
                    : p;
            }));
            const nombresExitosos = listaProspectos
                .filter(p => idsExitosos.includes(p.id))
                .map(p => p.nombres);

            Swal.fire({
                toast: true, position: "top-end", icon: "success",
                title: nombresExitosos.length === 1
                    ? "¡Prospecto Exitoso!"
                    : `${nombresExitosos.length} prospectos marcados como Exitosos`,
                html: nombresExitosos.length === 1
                    ? `<b>${nombresExitosos[0]}</b> tiene suscripción activa.`
                    : nombresExitosos.map(n => `• ${n}`).join("<br/>"),
                showConfirmButton: false, timer: 5000, timerProgressBar: true,
            });
        }

        setVerificando(false);
        verificacionRef.current = false;
    }, [baseURL]);

    // ════════════════════════════════════════════════════
    // CARGA INICIAL
    // ════════════════════════════════════════════════════
    useEffect(() => {
        const init = async () => {
            const lista = await fetchProspectos();
            fetchAsesores();
            await verificarMatchesAutomatico(lista);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefresh = async () => {
        const lista = await fetchProspectos(true);
        await verificarMatchesAutomatico(lista);
    };

    // ════════════════════════════════════════════════════
    // FILTROS DE GRILLA
    // ════════════════════════════════════════════════════
    useEffect(() => {
        const txt = busqueda.toLowerCase();
        const filtrados = datos.filter(p => {
            const coincideTexto  = !busqueda ||
                p.nroDocumento?.includes(txt) ||
                p.nombres?.toLowerCase().includes(txt) ||
                p.telefono?.includes(txt) ||
                p.correo?.toLowerCase().includes(txt) ||
                p.asesor?.toLowerCase().includes(txt);
            const coincideEstado = !estadoFiltro || p.estado === estadoFiltro;
            const coincideAsesor = !asesorFiltro || p.asesor === asesorFiltro;
            const fecha          = moment(p.fechaRegistro);
            const coincideInicio = !fechaInicio || fecha.isSameOrAfter(moment(fechaInicio));
            const coincideFin    = !fechaFin    || fecha.isSameOrBefore(moment(fechaFin));
            return coincideTexto && coincideEstado && coincideAsesor && coincideInicio && coincideFin;
        });
        setFiltrados(filtrados);
        setPagina(1);
    }, [datos, busqueda, estadoFiltro, asesorFiltro, fechaInicio, fechaFin]);

    // ════════════════════════════════════════════════════
    // DASHBOARD
    // ════════════════════════════════════════════════════
    const datosBase = datos.filter(p => {
        const f = moment(p.fechaRegistro);
        const coincideMes    = f.month() + 1 === parseInt(mesDash) && f.year() === parseInt(anioDash);
        const coincideAsesor = !asesorDash || p.asesor === asesorDash;
        return coincideMes && coincideAsesor;
    });
    const cntTotal      = datosBase.length;
    const cntInteresado = datosBase.filter(p => p.estado === "Interesado").length;
    const cntExitoso    = datosBase.filter(p => p.estado === "Exitoso").length;
    const cntDescartado = datosBase.filter(p => p.estado === "Descartado").length;

    // ════════════════════════════════════════════════════
    // PAGINACIÓN
    // ════════════════════════════════════════════════════
    const totalPaginas   = Math.ceil(datosFiltrados.length / elementosPorPagina);
    const indexInicio    = (paginaActual - 1) * elementosPorPagina;
    const datosPaginados = datosFiltrados.slice(indexInicio, indexInicio + elementosPorPagina);
    const rangoInicio    = Math.max(1, Math.min(paginaActual - 2, totalPaginas - 4));
    const rangoFin       = Math.min(totalPaginas, rangoInicio + 4);
    const paginasV       = Array.from({ length: rangoFin - rangoInicio + 1 }, (_, i) => rangoInicio + i);

    const limpiarFiltros = () => {
        setBusqueda(""); setEstadoFiltro(""); setAsesorFiltro("");
        setFechaInicio(""); setFechaFin("");
    };

    // ════════════════════════════════════════════════════
    // MATCH DOCUMENTO (modal edición)
    // ════════════════════════════════════════════════════
    const buscarDocumento = async (nroDocumento) => {
        if (!nroDocumento || nroDocumento.length < 5) { setMatchCedula(null); return; }
        setBuscandoCed(true);
        try {
            const resp = await axios.get(`${baseURL}/api/Prospectos/BuscarSuscriptorPorNroDocumento`, {
                params: { nroDocumento }
            });
            setMatchCedula(resp.data || { noMatch: true });
        } catch {
            setMatchCedula({ noMatch: true });
        } finally {
            setBuscandoCed(false);
        }
    };

    // ════════════════════════════════════════════════════
    // ENVIAR CORREO DE INVITACIÓN (manual desde grilla/detalle)
    // ════════════════════════════════════════════════════
    const enviarCorreo = async (p) => {
        if (!p.correo) {
            Swal.fire({
                icon: "warning", title: "Sin correo",
                text: "Este prospecto no tiene correo electrónico registrado.",
                confirmButtonColor: "#1a56db",
            });
            return;
        }
        const { isConfirmed } = await Swal.fire({
            title: "Enviar invitación",
            html: `¿Enviar el correo de invitación a suscripción a <br><b>${p.nombres}</b>?<br>
                   <small style="color:#6b7280;">${p.correo}</small>`,
            icon: "question", showCancelButton: true,
            confirmButtonColor: "#059669", cancelButtonColor: "#6c757d",
            confirmButtonText: "Sí, enviar", cancelButtonText: "Cancelar",
        });
        if (!isConfirmed) return;

        setEnviandoCorreo(p.id);
        try {
            await axios.post(`${baseURL}/api/Prospectos/EnviarCorreoProspecto`, {
                id: p.id, correo: p.correo, nombres: p.nombres, nroDocumento: p.nroDocumento,
            });
            Swal.fire({
                toast: true, position: "top-end", icon: "success",
                title: "Correo enviado correctamente",
                html: `Invitación enviada a <b>${p.correo}</b>`,
                showConfirmButton: false, timer: 3500, timerProgressBar: true,
            });
        } catch (err) {
            const msg = err?.response?.data?.mensaje || "Error al enviar el correo.";
            Swal.fire({ icon: "error", title: "Error", text: msg, confirmButtonColor: "#1a56db" });
        } finally {
            setEnviandoCorreo(null);
        }
    };

    // ════════════════════════════════════════════════════
    // ABRIR MODALES
    // ════════════════════════════════════════════════════
    const abrirCrear = () => {
        setModoEdicion(false);
        setFormulario(formularioVacio);
        setMatchCedula(null);
        setEnviarCorreoAlGuardar(false);
        setModalVisible(true);
    };

    const abrirEditar = (p) => {
        if (esBloqueado(p)) return;
        setModoEdicion(true);
        setProspectoSel(p);
        setFormulario({
            nroDocumento: p.nroDocumento, nombres: p.nombres,
            telefono: p.telefono, correo: p.correo,
            estado: p.estado, observaciones: p.observaciones,
            nomina: p.nomina ?? p.Nomina ?? "NO",
        });
        setMatchCedula(null);
        setEnviarCorreoAlGuardar(p.estado === "Interesado");
        buscarDocumento(p.nroDocumento);
        setModalVisible(true);
    };

    const abrirDetalle = async (p) => {
        setProspectoSel(p);
        setModalDetVisible(true);

        // Si es Exitoso, cargar / mostrar el plan
        if (p.docuMetoVerificado) {
            if (p.plan) {
                // Ya viene cacheado en el objeto
                setPlanDetalle(p.plan);
            } else {
                // Consultar al backend y cachear resultado
                setPlanDetalle(null);
                setCargandoPlanDetalle(true);
                try {
                    const resp = await axios.get(
                        `${baseURL}/api/Prospectos/BuscarSuscriptorPorNroDocumento`,
                        { params: { nroDocumento: p.nroDocumento } }
                    );
                    const planObtenido = (resp.data && !resp.data.noMatch) ? (resp.data.plan || null) : null;
                    setPlanDetalle(planObtenido);
                    // Cachear para evitar re-consultas
                    setDatos(prev => prev.map(d =>
                        d.id === p.id ? { ...d, plan: planObtenido } : d
                    ));
                } catch (err) {
                    console.error("[Detalle] Error buscando plan:", err);
                    setPlanDetalle(null);
                } finally {
                    setCargandoPlanDetalle(false);
                }
            }
        } else {
            setPlanDetalle(null);
        }
    };

    // ════════════════════════════════════════════════════
    // GUARDAR (crear / editar)
    // ════════════════════════════════════════════════════
    const guardar = async () => {
        const { nroDocumento, nombres, telefono, correo } = formulario;
        if (!nroDocumento || !nombres || !telefono || !correo) {
            Swal.fire({
                icon: "warning", title: "Campos requeridos",
                text: "Nro. Documento, nombres, teléfono y correo son obligatorios.",
                confirmButtonColor: "#1a56db",
            });
            return;
        }

        const hayMatch            = matchCedula && !matchCedula.noMatch;
        const estadoFinal         = hayMatch ? "Exitoso" : formulario.estado;
        const documentoVerificado = hayMatch;

        try {
            if (modoEdicion) {
                await axios.put(`${baseURL}/api/Prospectos/ActualizarProspecto`, {
                    id:                  prospectoSel.id,
                    nombres:             formulario.nombres,
                    telefono:            formulario.telefono,
                    correo:              formulario.correo,
                    estado:              estadoFinal,
                    observaciones:       formulario.observaciones,
                    documentoVerificado: documentoVerificado,
                    nomina:              formulario.nomina,
                });

                // Cachear plan si hay match
                if (hayMatch && matchCedula.plan) {
                    setDatos(prev => prev.map(p =>
                        p.id === prospectoSel.id
                            ? { ...p, estado: "Exitoso", docuMetoVerificado: true, plan: matchCedula.plan }
                            : p
                    ));
                }

                await fetchProspectos(true);
                setModalVisible(false);

                if (hayMatch) {
                    Swal.fire({
                        icon: "success", title: "¡Prospecto Exitoso!",
                        text: `Se verificó la suscripción de ${nombres}. El prospecto quedó como Exitoso.`,
                        confirmButtonColor: "#059669",
                    });
                } else if (estadoFinal === "Interesado" && enviarCorreoAlGuardar && correo) {
                    try {
                        await axios.post(`${baseURL}/api/Prospectos/EnviarCorreoProspecto`, {
                            id: prospectoSel.id, correo, nombres, nroDocumento,
                        });
                        Swal.fire({
                            toast: true, position: "top-end", icon: "success",
                            title: "Prospecto actualizado",
                            html: `Correo de invitación enviado a <b>${correo}</b>`,
                            showConfirmButton: false, timer: 4000, timerProgressBar: true,
                        });
                    } catch (errCorreo) {
                        const msgCorreo = errCorreo?.response?.data?.error || errCorreo?.response?.data?.mensaje || errCorreo.message;
                        Swal.fire({
                            icon: "warning", title: "Prospecto actualizado",
                            html: `El prospecto fue guardado correctamente, pero <b>no se pudo enviar el correo</b>.<br>
                                   <small style="color:#6b7280;font-size:0.82rem;">${msgCorreo}</small><br><br>
                                   Puedes reenviarlo desde el botón <b>✉</b> en la grilla.`,
                            confirmButtonColor: "#1a56db", confirmButtonText: "Entendido",
                        });
                    }
                } else {
                    Swal.fire({
                        toast: true, position: "top-end", icon: "success",
                        title: "Prospecto actualizado", showConfirmButton: false, timer: 2500,
                    });
                }

            } else {
                await axios.post(`${baseURL}/api/Prospectos/CrearProspecto`, {
                    nroDocumento:  formulario.nroDocumento,
                    nombres:       formulario.nombres,
                    telefono:      formulario.telefono,
                    correo:        formulario.correo,
                    observaciones: formulario.observaciones,
                    nomina:        formulario.nomina,
                    asesor:        userName,
                    fechaRegistro: moment().format("YYYY-MM-DD"),
                });

                await fetchProspectos(true);
                setModalVisible(false);

                Swal.fire({
                    toast: true, position: "top-end", icon: "success",
                    title: "Prospecto registrado correctamente",
                    showConfirmButton: false, timer: 3000, timerProgressBar: true,
                });
            }

        } catch (err) {
            const msg = err?.response?.data?.mensaje || err?.response?.data?.error || "Error al guardar el prospecto.";
            Swal.fire({ icon: "error", title: "Error al guardar", text: msg, confirmButtonColor: "#1a56db" });
        }
    };

    // ════════════════════════════════════════════════════
    // ELIMINAR (solo admin, solo no-Exitoso)
    // ════════════════════════════════════════════════════
    const eliminar = async (p) => {
        const { isConfirmed } = await Swal.fire({
            title: `¿Eliminar a ${p.nombres}?`, text: "Esta acción no se puede deshacer.",
            icon: "warning", showCancelButton: true,
            confirmButtonColor: "#c81e1e", cancelButtonColor: "#6c757d",
            confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar",
        });
        if (!isConfirmed) return;
        try {
            await axios.post(`${baseURL}/api/Prospectos/EliminarProspecto?id=${p.id}`);
            await fetchProspectos(true);
            Swal.fire({ toast: true, position: "top-end", icon: "success",
                title: "Prospecto eliminado", showConfirmButton: false, timer: 2000 });
        } catch (err) {
            const msg = err?.response?.data?.mensaje || err?.response?.data?.error || "Error al eliminar.";
            Swal.fire({ icon: "error", title: "Error", text: msg, confirmButtonColor: "#1a56db" });
        }
    };

    // ════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════
    return (
        <div>
            <div className="telefonos-card">

                {/* ── Header ── */}
                <div className="card-header-custom d-flex align-items-center justify-content-between">
                    <h4 className="card-title-custom">
                        <FaUsers className="mr-2" />
                        Módulo de Prospectos
                        <span className="prosp-badge-count ml-3">{datos.length} registros</span>
                    </h4>
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                        <button className="btn-refresh-prosp" onClick={handleRefresh}
                            disabled={verificando} title="Verificar suscripciones y actualizar">
                            <FaSyncAlt className={verificando ? "spin-icon" : ""} />
                            {verificando ? " Verificando..." : " Actualizar"}
                        </button>
                        <button className="btn-agregar-custom" onClick={abrirCrear}>
                            <FaUserPlus /> Nuevo Prospecto
                        </button>
                    </div>
                </div>

                <div className="form-section">

                    {verificando && (
                        <div className="prosp-verificando-banner">
                            <span className="fact-spinner" style={{ marginRight: 8 }} />
                            Verificando suscripciones de prospectos pendientes...
                        </div>
                    )}

                    {/* ── Dashboard ── */}
                    <div className="prosp-mes-wrap">
                        <FaStar style={{ color: "#f0ad4e" }} />
                        <span className="prosp-mes-label">Resumen —</span>
                        <select className="custom-input prosp-mes-select"
                            value={mesDash} onChange={e => setMesDash(e.target.value)}>
                            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                        <select className="custom-input prosp-mes-select"
                            value={anioDash} onChange={e => setAnioDash(e.target.value)}>
                            {[2024, 2025, 2026].map(a => <option key={a}>{a}</option>)}
                        </select>
                        {esAdmin && (
                            <>
                                <span className="prosp-mes-label" style={{ marginLeft: 6 }}>Asesor:</span>
                                <select className="custom-input prosp-mes-select"
                                    value={asesorDash} onChange={e => setAsesorDash(e.target.value)}>
                                    <option value="">Todos</option>
                                    {asesores.map(a => (
                                        <option key={a.nombreUsuario} value={a.nombreUsuario}>
                                            {a.nombresApellidos}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>

                    {/* ── Tarjetas ── */}
                    <div className="prosp-resumen-wrap">
                        {[
                            { label: "Total del mes", val: cntTotal,      icon: <FaUsers style={{ color: "#1a56db" }} />, bg: "#e8f0fe" },
                            { label: "Interesados",   val: cntInteresado,  icon: <FaStar style={{ color: "#28a745" }} />,  bg: "#d4edda" },
                            { label: "Exitosos",      val: cntExitoso,     icon: <FaTrophy style={{ color: "#059669" }} />, bg: "#d1fae5" },
                            { label: "Descartados",   val: cntDescartado,  icon: <FaTimesCircle style={{ color: "#6c757d" }} />, bg: "#e2e3e5" },
                        ].map(({ label, val, icon, bg }) => (
                            <div key={label} className="prosp-card-resumen">
                                <div className="prosp-card-icon" style={{ background: bg }}>{icon}</div>
                                <div>
                                    <p className="prosp-card-label">{label}</p>
                                    <p className="prosp-card-value">{val}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Filtros ── */}
                    <div className="fact-filtros-wrap">
                        <div className="usuarios-search-wrap fact-search">
                            <FaSearch className="usuarios-search-icon" />
                            <input type="text" className="custom-input usuarios-search-input"
                                placeholder="Buscar por documento, nombre, correo, asesor..."
                                autoComplete="off" value={busqueda}
                                onChange={e => setBusqueda(e.target.value)} />
                        </div>
                        <div className="fact-filter-item fact-filter-date">
                            <label className="fact-filter-label">Desde</label>
                            <input type="date" className="custom-input fact-date-input"
                                value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
                        </div>
                        <div className="fact-filter-item fact-filter-date">
                            <label className="fact-filter-label">Hasta</label>
                            <input type="date" className="custom-input fact-date-input"
                                value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
                        </div>
                        <div className="fact-filter-item fact-filter-estado">
                            <label className="fact-filter-label">Estado</label>
                            <select className="custom-input fact-date-input"
                                value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
                                <option value="">Todos</option>
                                {TODOS_LOS_ESTADOS.map(e => <option key={e}>{e}</option>)}
                            </select>
                        </div>
                        {esAdmin && (
                            <div className="fact-filter-item fact-filter-estado">
                                <label className="fact-filter-label">Asesor</label>
                                <select className="custom-input fact-date-input"
                                    value={asesorFiltro} onChange={e => setAsesorFiltro(e.target.value)}>
                                    <option value="">Todos</option>
                                    {asesores.map(a => (
                                        <option key={a.userName} value={a.nombreUsuario}>{a.nombresApellidos}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <button className="btn-limpiar-filtros" onClick={limpiarFiltros}>
                            <FaFilter style={{ marginRight: 5 }} /> Limpiar
                        </button>
                    </div>

                    {/* ── Tabla ── */}
                    <div className="table-responsive usuarios-table-wrap">
                        <table className="usuarios-table prosp-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Nro. Documento</th>
                                    <th>Nombres y Apellidos</th>
                                    <th>Teléfono</th>
                                    {esAdmin && <th>Asesor</th>}
                                    <th>Nómina</th>
                                    <th>Estado</th>
                                    <th>Fecha Registro</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {datosPaginados.length > 0 ? (
                                    datosPaginados.map((p, idx) => {
                                        const bloqueado    = esBloqueado(p);
                                        const enviandoEste = enviandoCorreo === p.id;
                                        return (
                                            <tr key={p.id}>
                                                <td className="prosp-id">{indexInicio + idx + 1}</td>

                                                <td>
                                                    <span className="prosp-cedula-wrap">
                                                        {p.docuMetoVerificado
                                                            ? <FaCheckCircle className="prosp-cedula-ok" title="Suscripción verificada" />
                                                            : <FaTimesCircle className="prosp-cedula-warn" title="Sin suscripción registrada" />}
                                                        {p.nroDocumento}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className="prosp-nombre">{p.nombres}</div>
                                                    <div className="prosp-sub">{p.correo}</div>
                                                </td>

                                                <td>{p.telefono}</td>

                                                {esAdmin && (
                                                    <td><span className="prosp-asesor">{p.asesor}</span></td>
                                                )}

                                                <td>
                                                    <span className={`prosp-nomina-badge ${(p.nomina ?? p.Nomina) === "SI" ? "prosp-nomina-si" : "prosp-nomina-no"}`}>
                                                        {p.nomina ?? p.Nomina ?? "NO"}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span className={BADGE_CLASS[p.estado] || "badge-estado"}>
                                                        <span className="badge-dot"></span>
                                                        {p.estado}
                                                    </span>
                                                </td>

                                                <td style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                                                    {moment(p.fechaRegistro).format("DD/MM/YYYY")}
                                                </td>

                                                <td>
                                                    <div className="acciones-wrap">
                                                        <button className="btn-accion-tabla btn-ver-prosp"
                                                            title="Ver detalle" onClick={() => abrirDetalle(p)}>
                                                            <FaEye /> Ver
                                                        </button>

                                                        {!bloqueado && (
                                                            <button
                                                                className="btn-accion-tabla btn-correo-prosp"
                                                                title="Enviar invitación por correo"
                                                                onClick={() => enviarCorreo(p)}
                                                                disabled={enviandoEste}
                                                            >
                                                                {enviandoEste ? <span className="fact-spinner" /> : <FaEnvelope />}
                                                            </button>
                                                        )}

                                                        {!bloqueado && (
                                                            <button className="btn-accion-tabla btn-editar-prosp"
                                                                title="Editar Prospecto" onClick={() => abrirEditar(p)}>
                                                                <FaEdit />
                                                            </button>
                                                        )}

                                                        {!bloqueado && esAdmin && (
                                                            <button className="btn-accion-tabla btn-eliminar-prosp"
                                                                title="Eliminar Prospecto" onClick={() => eliminar(p)}>
                                                                <FaTrash />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={esAdmin ? 9 : 8} className="sin-resultados">
                                            <FaSearch style={{ marginRight: 6 }} />No se encontraron prospectos
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Paginador ── */}
                    {totalPaginas > 1 && (
                        <div className="fact-paginacion">
                            <span className="fact-pag-info">
                                Mostrando {indexInicio + 1}–{Math.min(indexInicio + elementosPorPagina, datosFiltrados.length)} de {datosFiltrados.length} registros
                            </span>
                            <div className="paginacion-wrap">
                                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPagina(1)}>«</button>
                                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPagina(p => p - 1)}>‹</button>
                                {rangoInicio > 1 && <span className="btn-pagina btn-pag-dots">…</span>}
                                {paginasV.map(n => (
                                    <button key={n} className={`btn-pagina ${paginaActual === n ? "activa" : ""}`}
                                        onClick={() => setPagina(n)}>{n}</button>
                                ))}
                                {rangoFin < totalPaginas && <span className="btn-pagina btn-pag-dots">…</span>}
                                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPagina(p => p + 1)}>›</button>
                                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPagina(totalPaginas)}>»</button>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* ════ MODAL CREAR / EDITAR ════ */}
            {modalVisible && (
                <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModalVisible(false); }}>
                    <div className="modal-usuarios" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>

                        <div className="modal-header-custom">
                            <h5 className="modal-title-custom">
                                <FaUserPlus className="mr-2" />
                                {modoEdicion ? "Editar Prospecto" : "Nuevo Prospecto"}
                            </h5>
                            <button className="modal-close" onClick={() => setModalVisible(false)}>×</button>
                        </div>

                        <div className="modal-body-custom">

                            {modoEdicion && matchCedula && !matchCedula.noMatch && (
                                <div className="prosp-bloqueado-aviso">
                                    <FaTrophy />
                                    <span>Se detectó una suscripción activa. Al guardar el prospecto quedará como <strong>Exitoso</strong> y no podrá editarse.</span>
                                </div>
                            )}

                            <div className="row">
                                {/* Nro. Documento */}
                                <div className="col-md-6">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Nro. Documento *</label>
                                        <div className="custom-input-group">
                                            <span className="custom-prepend"></span>
                                            <input type="text" className="custom-input"
                                                placeholder="Ej: 12345678"
                                                value={formulario.nroDocumento}
                                                readOnly={modoEdicion}
                                                style={modoEdicion ? { background: "#828385", cursor: "not-allowed" } : {}}
                                                onChange={e => {
                                                    if (modoEdicion) return;
                                                    setFormulario(f => ({ ...f, nroDocumento: e.target.value }));
                                                }}
                                            />
                                            {buscandoCed && (
                                                <span className="custom-prepend" style={{ borderLeft: "none", background: "white" }}>
                                                    <span className="fact-spinner" />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Nombres */}
                                <div className="col-md-6">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Nombres y Apellidos *</label>
                                        <input type="text" className="custom-input" style={{ width: "100%" }}
                                            placeholder="Nombre completo del prospecto"
                                            value={formulario.nombres}
                                            onChange={e => setFormulario(f => ({ ...f, nombres: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Teléfono */}
                                <div className="col-md-6">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Teléfono *</label>
                                        <input type="text" className="custom-input" style={{ width: "100%" }}
                                            placeholder="Ej: 3001234567"
                                            value={formulario.telefono}
                                            onChange={e => setFormulario(f => ({ ...f, telefono: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Correo */}
                                <div className="col-md-6">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Correo electrónico *</label>
                                        <input type="email" className="custom-input" style={{ width: "100%" }}
                                            placeholder="correo@ejemplo.com"
                                            value={formulario.correo}
                                            onChange={e => setFormulario(f => ({ ...f, correo: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Estado */}
                                <div className="col-md-6">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Estado</label>
                                        {!modoEdicion ? (
                                            <input className="custom-input"
                                                style={{ width: "100%", background: "#f8f9fa", cursor: "not-allowed" }}
                                                value="Nuevo" readOnly />
                                        ) : matchCedula && !matchCedula.noMatch ? (
                                            <input className="custom-input"
                                                style={{ width: "100%", background: "#d1fae5", color: "#065f46", fontWeight: 700, cursor: "not-allowed" }}
                                                value="Exitoso (automático)" readOnly />
                                        ) : (
                                            <select className="custom-input" style={{ width: "100%" }}
                                                value={formulario.estado}
                                                onChange={e => {
                                                    const nuevoEstado = e.target.value;
                                                    setFormulario(f => ({ ...f, estado: nuevoEstado }));
                                                    setEnviarCorreoAlGuardar(nuevoEstado === "Interesado");
                                                }}>
                                                {ESTADOS_EDITABLES.map(e => <option key={e}>{e}</option>)}
                                            </select>
                                        )}
                                     </div>
                                 </div>

                                 {/* Nómina */}
                                <div className="col-md-6">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Nomina</label>
                                        <div className="prosp-nomina-options">
                                            {["SI", "NO"].map(opcion => (
                                                <label key={opcion} className="prosp-nomina-option">
                                                    <input
                                                        type="radio"
                                                        name="nomina"
                                                        value={opcion}
                                                        checked={formulario.nomina === opcion}
                                                        onChange={e => setFormulario(f => ({ ...f, nomina: e.target.value }))}
                                                    />
                                                    {opcion}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                 </div>

                                 {/* Observaciones */}
                                <div className="col-md-12">
                                    <div className="custom-form-group">
                                        <label className="custom-label">Observaciones</label>
                                        <textarea className="custom-input"
                                            style={{ width: "100%", height: "80px", resize: "vertical" }}
                                            placeholder="Notas adicionales sobre el prospecto..."
                                            value={formulario.observaciones}
                                            onChange={e => setFormulario(f => ({ ...f, observaciones: e.target.value }))} />
                                    </div>
                                </div>

                                {/* Checkbox enviar correo */}
                                {modoEdicion && formulario.estado === "Interesado" && !(matchCedula && !matchCedula.noMatch) && (
                                    <div className="col-md-12">
                                        <div className="prosp-correo-check-wrap">
                                            <label className="prosp-correo-check-label">
                                                <input
                                                    type="checkbox"
                                                    className="prosp-correo-check-input"
                                                    checked={enviarCorreoAlGuardar}
                                                    onChange={e => setEnviarCorreoAlGuardar(e.target.checked)}
                                                />
                                                <span className="prosp-correo-check-icon"><FaEnvelope /></span>
                                                Enviar correo de invitación al guardar
                                                <span className="prosp-correo-check-sub">
                                                    Se enviará a <strong>{formulario.correo || "—"}</strong>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer-custom">
                            <button className="btn-modal-cancelar" onClick={() => setModalVisible(false)}>Cancelar</button>
                            <button className="btn-save-custom" onClick={guardar}>
                                {modoEdicion
                                    ? (formulario.estado === "Interesado" && enviarCorreoAlGuardar
                                        ? "Actualizar y Enviar Correo"
                                        : "Actualizar Prospecto")
                                    : "Registrar Prospecto"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════ MODAL DETALLE ════ */}
            {modalDetVisible && prospectoSel && (
                <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModalDetVisible(false); }}>
                    <div className="modal-usuarios" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>

                        <div className="modal-header-custom">
                            <h5 className="modal-title-custom"><FaEye className="mr-2" /> Detalle del Prospecto</h5>
                            <button className="modal-close" onClick={() => setModalDetVisible(false)}>×</button>
                        </div>

                        <div className="modal-body-custom">
                            {esBloqueado(prospectoSel) && (
                                <div className="prosp-bloqueado-aviso" style={{ marginBottom: "1rem" }}>
                                    <FaTrophy />
                                    <span>Prospecto exitoso — La suscripción fue verificada.</span>
                                </div>
                            )}

                            <div className="fact-detalle-grid">
                                {[
                                    ["Nro. Documento", prospectoSel.nroDocumento],
                                    ["Estado",
                                        <span className={BADGE_CLASS[prospectoSel.estado] || "badge-estado"}>
                                            <span className="badge-dot"></span>{prospectoSel.estado}
                                        </span>
                                    ],
                                    ["Nombres",        prospectoSel.nombres, true],
                                    ["Teléfono",       prospectoSel.telefono],
                                    ["Correo",         prospectoSel.correo],
                                    ["Nómina",         prospectoSel.nomina ?? prospectoSel.Nomina ?? "NO"],
                                    ["Asesor",         <span className="prosp-asesor">{prospectoSel.asesor}</span>],
                                    ["Fecha registro", moment(prospectoSel.fechaRegistro).format("DD/MM/YYYY")],
                                    // ── Suscripción con plan ──────────────────────────────
                                    ["Suscripción",
                                        prospectoSel.docuMetoVerificado ? (
                                            <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                <FaCheckCircle style={{ color: "#059669", flexShrink: 0 }} />
                                                <span style={{ color: "#059669", fontWeight: 600 }}>Verificada</span>
                                                {cargandoPlanDetalle
                                                    ? <span className="fact-spinner" style={{ marginLeft: 2 }} />
                                                    : planDetalle
                                                        ? <span className="prosp-plan-badge">{planDetalle}</span>
                                                        : null
                                                }
                                            </span>
                                        ) : (
                                            <span style={{ color: "#f0ad4e", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                                                <FaTimesCircle /> No verificada
                                            </span>
                                        )
                                    ],
                                    ["Observaciones", prospectoSel.observaciones || "—", true],
                                ].map(([label, val, full]) => (
                                    <div key={label} className={`fact-detalle-item${full ? " fact-detalle-full" : ""}`}>
                                        <span className="fact-detalle-label">{label}</span>
                                        <span className="fact-detalle-value">{val}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="modal-footer-custom">
                            <button className="btn-modal-cancelar" onClick={() => setModalDetVisible(false)}>Cerrar</button>

                            {!esBloqueado(prospectoSel) && (
                                <button className="btn-correo-modal"
                                    onClick={() => { setModalDetVisible(false); enviarCorreo(prospectoSel); }}
                                    disabled={enviandoCorreo === prospectoSel.id}>
                                    <FaEnvelope className="mr-1" /> Enviar Invitación
                                </button>
                            )}

                            {!esBloqueado(prospectoSel) && (
                                <button className="btn-save-custom"
                                    onClick={() => { setModalDetVisible(false); abrirEditar(prospectoSel); }}>
                                    <FaEdit className="mr-1" /> Editar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Prospectos;
